// cm-coding: The browser may store Workday credentials and bounded feed settings
// but must not retrieve requisitions during configuration. Expose no preview
// request from this client module so UI code cannot invoke attachment-heavy
// SOAP outside the explicit background pull action.

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import { connectorPath } from '../../api'
import type {
  ConnectorFeedMutation,
  ConnectorInitialSync,
  ConnectorSchedule,
} from '../../types'
import type {
  WorkdayConnectionStatus,
  WorkdayFeedOptions,
  WorkdayWorkspaceCredentials,
} from '../types'

export interface CreateWorkdayFeedRequest {
  initialSync: ConnectorInitialSync
  schedule: ConnectorSchedule
  sourceDisplayFieldKeys: string[]
  workday: WorkdayFeedOptions
}

const feedRequest = (request: CreateWorkdayFeedRequest) => ({
  provider: 'workday' as const,
  direction: 'inbound' as const,
  remoteFolderId: 'requisitions',
  remoteFolderName: 'Workday requisitions',
  ...request,
})

export const createWorkdayFeed = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  request: CreateWorkdayFeedRequest,
) => (await api.post<ConnectorFeedMutation>(
  `${connectorPath(orgId, vaultId)}/feeds`,
  feedRequest(request),
)).data

export const updateWorkdayFeed = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  feedId: string,
  schedule: ConnectorSchedule,
  sourceDisplayFieldKeys: string[],
  workday: WorkdayFeedOptions,
) => (await api.put<ConnectorFeedMutation>(
  `${connectorPath(orgId, vaultId)}/feeds/${encodeURIComponent(feedId)}/workday`,
  { schedule, sourceDisplayFieldKeys, workday },
)).data

export const getWorkdayConnection = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => (await api.get<WorkdayConnectionStatus>(
  `${connectorPath(orgId, vaultId)}/workday/connection`,
)).data

export const saveWorkdayConnection = (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  credentials: WorkdayWorkspaceCredentials,
) => api.put(
  `${connectorPath(orgId, vaultId)}/workday/connection`,
  credentials,
)

export const deleteWorkdayConnection = (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => api.delete(`${connectorPath(orgId, vaultId)}/workday/connection`)
